if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
        
        const addToCartButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const buyNowButton = document.getElementById(`BuyNowButton-${this.sectionId}`);
        const isAddToCartDisabled = addToCartButton?.hasAttribute('disabled') ?? true;

        if (isAddToCartDisabled) {
            if (addToCartButton) {
              addToCartButton.setAttribute('hidden', 'hidden');
            }
            if (buyNowButton) {
              buyNowButton.setAttribute('hidden', 'hidden');
            }
        } else {
            if (addToCartButton) {
              addToCartButton.removeAttribute('hidden');
            }
            if (buyNowButton) {
              buyNowButton.removeAttribute('hidden');
            }
          }
    
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

         
          const addToCartButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const buyNowButton = html.getElementById(`BuyNowButton-${this.sectionId}`);
          const isAddToCartDisabled = addToCartButton?.hasAttribute('disabled') ?? true;
        
          if (buyNowButton) {
           if (isAddToCartDisabled) {
              buyNowButton.setAttribute('disabled', 'disabled');
            
            } else {
              buyNowButton.removeAttribute('disabled');
            }
          }
          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
           
            window.variantStrings.soldOut
          );

          this.productForm.toggleButton(
            document.getElementById(`BuyNowButton-${this.sectionId}`),
            isAddToCartDisabled,
            window.variantStrings.soldOut
          );

            this.productForm.toggleButton(
            document.getElementById(`ProductSubmitButton-${this.sectionId}`),
            isAddToCartDisabled,
            window.variantStrings.soldOut
          );

         
          
         

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

    }
  );
}

(() => {
  const isFlm001ProductPage = () => {
    const path = window.location.pathname.replace(/\/$/, '');
    return path === '/products/flm-001' || path === '/products/flm-001-water-flow-meter' || path === '/products/flm-001-flow-meter';
  };

  if (!isFlm001ProductPage()) return;

  const fallbackStyleId = 'flm-001-pdp-fallback-style';
  const shippingHtml = `
    <p><strong>Free shipping.</strong> FLM-001 ships free across the contiguous U.S. Orders placed before 2pm ET ship the same business day; most customers receive their package within 2-7 business days via USPS or UPS Ground. Tracking is emailed as soon as your label is created.</p>
    <p><strong>30-day money-back guarantee.</strong> Try it risk-free for 30 days. If it is not the right fit, send it back and we will refund the full purchase price within 3 business days of receiving the unit.</p>
    <p><strong>1-year limited warranty.</strong> FLM-001 is covered against manufacturing defects for one full year from delivery. If something goes wrong, call <a href="tel:+18884276838">888-427-6838</a> or email <a href="mailto:support@soildrops.com">support@soildrops.com</a> and we will repair or replace it.</p>
  `;

  const injectFallbackStyles = () => {
    if (document.getElementById(fallbackStyleId)) return;

    const style = document.createElement('style');
    style.id = fallbackStyleId;
    style.textContent = `
      .product-detail-section .flm-feature-list {
        display: grid;
        gap: 0.8rem;
        margin: 0 0 1.8rem;
        padding-left: 1.2rem;
      }

      .product-detail-section .flm-feature-list li {
        line-height: 1.45;
        padding-left: 0.2rem;
      }

      .product-detail-section .flm-spec-card {
        border: 1px solid rgba(0, 75, 67, 0.16);
        border-radius: 8px;
        margin-top: 2rem;
        overflow: hidden;
        background: #ffffff;
        box-shadow: 0 12px 32px rgba(0, 75, 67, 0.08);
      }

      .product-detail-section .flm-spec-below {
        margin-top: 3.2rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-card {
        margin-top: 0;
      }

      .product-detail-section .flm-spec-card__header {
        background: linear-gradient(135deg, rgba(0, 75, 67, 0.96), rgba(89, 157, 45, 0.88));
        color: #ffffff;
        padding: 1.4rem 1.6rem;
      }

      .product-detail-section .flm-spec-eyebrow {
        color: rgba(255, 255, 255, 0.78);
        font-size: 0.82rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        margin: 0 0 0.35rem;
        text-transform: uppercase;
      }

      .product-detail-section .flm-spec-card__header h3 {
        color: #ffffff;
        font-size: clamp(1.35rem, 2vw, 1.7rem);
        line-height: 1.15;
        margin: 0;
      }

      .product-detail-section .flm-spec-highlights {
        display: grid;
        gap: 0.7rem;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        padding: 1rem 1rem 0;
      }

      .product-detail-section .flm-spec-highlight {
        background: #f5f9f2;
        border: 1px solid rgba(89, 157, 45, 0.2);
        border-radius: 8px;
        padding: 0.85rem;
      }

      .product-detail-section .flm-spec-highlight span {
        color: #476055;
        display: block;
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        margin-bottom: 0.2rem;
        text-transform: uppercase;
      }

      .product-detail-section .flm-spec-highlight strong {
        color: #004b43;
        display: block;
        font-size: 1rem;
        line-height: 1.2;
      }

      .product-detail-section .flm-spec-below .flm-spec-card__header {
        padding: 1.8rem 2rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-eyebrow {
        font-size: 1.15rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-card__header h3 {
        font-size: 2.8rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-highlights {
        gap: 1rem;
        padding: 1.4rem 1.4rem 0;
      }

      .product-detail-section .flm-spec-below .flm-spec-highlight {
        padding: 1.1rem 1.3rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-highlight span {
        font-size: 1.15rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-highlight strong {
        font-size: 2rem;
      }

      .product-detail-section .flm-spec-table-wrap {
        padding: 1rem;
      }

      .product-detail-section .flm-spec-table {
        border: 1px solid #dfe7df;
        border-collapse: separate;
        border-radius: 8px;
        border-spacing: 0;
        overflow: hidden;
        width: 100%;
      }

      .product-detail-section .flm-spec-table tr:nth-child(odd) {
        background: #fbfdf9;
      }

      .product-detail-section .flm-spec-table th,
      .product-detail-section .flm-spec-table td {
        border-bottom: 1px solid #e5ece4;
        font-size: 0.95rem;
        line-height: 1.35;
        padding: 0.78rem 0.9rem;
        text-align: left;
        vertical-align: top;
      }

      .product-detail-section .flm-spec-table tr:last-child th,
      .product-detail-section .flm-spec-table tr:last-child td {
        border-bottom: 0;
      }

      .product-detail-section .flm-spec-table th {
        color: #24463e;
        font-weight: 800;
        width: 42%;
      }

      .product-detail-section .flm-spec-table td {
        color: #1f2f2c;
        font-weight: 600;
      }

      .product-detail-section .flm-spec-below .flm-spec-table-wrap {
        padding: 1.4rem;
      }

      .product-detail-section .flm-spec-below .flm-spec-table th,
      .product-detail-section .flm-spec-below .flm-spec-table td {
        font-size: 1.55rem;
        line-height: 1.45;
        padding: 1.25rem 1.4rem;
      }

      @media screen and (max-width: 749px) {
        .product-detail-section .flm-spec-highlights {
          grid-template-columns: 1fr;
        }

        .product-detail-section .flm-spec-below {
          margin-top: 2rem;
        }

        .product-detail-section .flm-spec-below .flm-spec-card__header h3 {
          font-size: 2.2rem;
        }

        .product-detail-section .flm-spec-card__header,
        .product-detail-section .flm-spec-table-wrap {
          padding-left: 1rem;
          padding-right: 1rem;
        }

        .product-detail-section .flm-spec-table th,
        .product-detail-section .flm-spec-table td {
          display: block;
          width: 100%;
        }

        .product-detail-section .flm-spec-table th {
          border-bottom: 0;
          padding-bottom: 0.25rem;
        }

        .product-detail-section .flm-spec-table td {
          padding-top: 0;
        }

        .product-detail-section .flm-spec-below .flm-spec-table th,
        .product-detail-section .flm-spec-below .flm-spec-table td {
          font-size: 1.45rem;
          padding-left: 1rem;
          padding-right: 1rem;
        }
      }
    `;
    document.head.append(style);
  };

  const removeControllerSections = () => {
    document
      .querySelectorAll('.deal-section, .come-friend-section, .section-product-faq')
      .forEach((element) => {
        const wrapper = element.closest('.shopify-section');
        (wrapper || element).remove();
      });
  };

  const replaceShippingAccordion = () => {
    document.querySelectorAll('.product__accordion .accordion__content').forEach((content) => {
      const accordion = content.closest('.product__accordion');
      const heading = accordion?.querySelector('.accordion__title')?.textContent || '';
      const mentionsController = /Controller Starter Kit|Every Controller/i.test(content.textContent);

      if (mentionsController || /Shipping/i.test(heading)) {
        content.innerHTML = shippingHtml;
      }
    });
  };

  const removeInternalSpecRows = (scope = document) => {
    const internalLabels = new Set([
      'Master Carton',
      'Case Pack',
      'Master Carton Net Weight',
      'Master Carton Gross Weight',
    ]);

    scope.querySelectorAll('.flm-spec-table tr').forEach((row) => {
      const label = row.querySelector('th')?.textContent?.trim();
      if (internalLabels.has(label)) row.remove();
    });
  };

  const enhanceSpecsTable = () => {
    const infoBox = document.querySelector('.product-detail-summary .info-box, .info-box');
    if (!infoBox) return;

    const featureList = infoBox.querySelector('ul');
    featureList?.classList.add('flm-feature-list');

    const productRow = document.querySelector('.product-detail-section .container > .row');
    const specBelow = document.querySelector('.product-detail-section .flm-spec-below') || document.createElement('div');
    specBelow.className = 'flm-spec-below';

    if (!specBelow.isConnected && productRow) {
      productRow.after(specBelow);
    }

    const existingSpecCard = infoBox.querySelector('.flm-spec-card');
    if (existingSpecCard) {
      specBelow.append(existingSpecCard);
      existingSpecCard.querySelector('table')?.classList.add('flm-spec-table');
      removeInternalSpecRows(existingSpecCard);
      return;
    }

    const specHeading = Array.from(infoBox.querySelectorAll('h3')).find((heading) =>
      /Specifications/i.test(heading.textContent)
    );
    const specTable = specHeading?.nextElementSibling?.tagName === 'TABLE' ? specHeading.nextElementSibling : null;
    if (!specTable) return;

    specTable.classList.add('flm-spec-table');

    const specCard = document.createElement('section');
    specCard.className = 'flm-spec-card';
    specCard.setAttribute('aria-label', 'FLM-001 specifications');
    specCard.innerHTML = `
      <div class="flm-spec-card__header">
        <p class="flm-spec-eyebrow">Specifications</p>
        <h3>FLM-001 Water Flow Meter</h3>
      </div>
      <div class="flm-spec-highlights">
        <div class="flm-spec-highlight"><span>Accuracy</span><strong>&plusmn;5%</strong></div>
        <div class="flm-spec-highlight"><span>Pressure</span><strong>0&ndash;116 PSI</strong></div>
        <div class="flm-spec-highlight"><span>Flow Rate</span><strong>0.5&ndash;12 GPM</strong></div>
      </div>
      <div class="flm-spec-table-wrap"></div>
    `;

    specBelow.append(specCard);
    specCard.querySelector('.flm-spec-table-wrap').append(specTable);
    removeInternalSpecRows(specCard);
    specHeading.remove();
  };

  const applyFlm001Fallback = () => {
    injectFallbackStyles();
    removeControllerSections();
    replaceShippingAccordion();
    enhanceSpecsTable();
    removeInternalSpecRows();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyFlm001Fallback, { once: true });
  } else {
    applyFlm001Fallback();
  }

  window.addEventListener('product-info:loaded', applyFlm001Fallback);

  let fallbackRuns = 0;
  const fallbackInterval = window.setInterval(() => {
    applyFlm001Fallback();
    fallbackRuns += 1;
    if (fallbackRuns >= 6) window.clearInterval(fallbackInterval);
  }, 1000);
})();
